import { Subject } from 'rxjs';

import { Transaction, TransactionLineItemAddRequest } from '../../features/api';
import { PacSignatureVerifyMessage } from '../../features/pac-signature-verify/pacSignatureVerifyMessage';

export interface CardPreAuthSuccessEvent {
  transactionId: number;
  preAuthId: number | null;
  amountAuthorized: number;
  authCode: string | null;
  cardScheme: string | null;
  expiresAt: string | null;
  cardRequestId: number | null;
  transaction: Transaction | null;
}

export interface CardPreAuthCapturedEvent {
  transactionId: number;
  preAuthId: number;
  amountCaptured: number;
  transaction: Transaction | null;
}

export interface CardPreAuthIncrementedEvent {
  transactionId: number;
  preAuthId: number;
  newAmountAuthorized: number;
  transaction: Transaction | null;
}

export interface CardPreAuthReleasedEvent {
  transactionId: number;
  preAuthId: number;
  transaction: Transaction | null;
}

const testSubject = new Subject<number>();
const killSubject = new Subject<boolean>();
const cardPaymentSuccessSubject = new Subject<{ transactionId: number; result: string; transaction: Transaction | null }>();
const cardPaymentFailureSubject = new Subject<number>();
const cardSignatureSubject = new Subject<PacSignatureVerifyMessage>();
const cardNotificationSubject = new Subject<{ transactionId: number; notification: string; terminalId: string }>();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const errorButtonClickedSubject = new Subject<any>();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const errorButtonAlternateClickedSubject = new Subject<any>();
const cardResultStatusSubject = new Subject<CardPaymentStatusItem>();
const addToBasketSubject = new Subject<TransactionLineItemAddRequest>();
const addToBasketCompleteSubject = new Subject<{ success: boolean }>();
const layoutVersionSubject = new Subject<number>();
const failedDispensePrintsChangedSubject = new Subject<void>();
const dispenseLogCreatedSubject = new Subject<DispenseLogEvent>();
const dispenseLogStateChangedSubject = new Subject<DispenseLogStateChangedEvent>();
const dispenseLogVoidedSubject = new Subject<DispenseLogEvent>();
const dispenseLogVoidAcknowledgedSubject = new Subject<DispenseLogEvent>();
const dispenseLogOverdueSubject = new Subject<DispenseLogOverdueEvent>();
const dispenseCompleteSubject = new Subject<DispenseCompleteEvent>();
const dispenseReadySubject = new Subject<DispenseCompleteEvent>();
const cardFailureLockSubject = new Subject<CardFailureLockEvent>();
const cardPreAuthSuccessSubject = new Subject<CardPreAuthSuccessEvent>();
const cardPreAuthFailureSubject = new Subject<CardFailureLockEvent>();
const cardPreAuthCapturedSubject = new Subject<CardPreAuthCapturedEvent>();
const cardPreAuthIncrementedSubject = new Subject<CardPreAuthIncrementedEvent>();
const cardPreAuthReleasedSubject = new Subject<CardPreAuthReleasedEvent>();

export const appSubscriptionService = {
  testNext: (val: number) => testSubject.next(val),
  testObservable: () => testSubject.asObservable(),
  killNext: (val: boolean) => killSubject.next(val),
  killObservable: () => killSubject.asObservable(),
  cardPaymentSuccessNext: (val: { transactionId: number; result: string; transaction: Transaction | null }) =>
    cardPaymentSuccessSubject.next(val),
  cardPaymentSuccessObservable: () => cardPaymentSuccessSubject.asObservable(),
  cardSignatureNext: (val: PacSignatureVerifyMessage) => cardSignatureSubject.next(val),
  cardSignatureObservable: () => cardSignatureSubject.asObservable(),
  cardNotificationNext: (val: { transactionId: number; notification: string; terminalId: string }) => cardNotificationSubject.next(val),
  cardNotificationObservable: () => cardNotificationSubject.asObservable(),
  cardPaymentFailureNext: (val: number) => cardPaymentFailureSubject.next(val),
  cardPaymentFailureObservable: () => cardPaymentFailureSubject.asObservable(),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errorButtonClickedNext: (val: any) => errorButtonClickedSubject.next(val),
  errorButtonClickedObservable: () => errorButtonClickedSubject.asObservable(),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errorButtonAlternateClickedNext: (val: any) => errorButtonAlternateClickedSubject.next(val),
  errorButtonAlternateClickedObservable: () => errorButtonAlternateClickedSubject.asObservable(),
  cardResultStatusNext: (val: CardPaymentStatusItem) => cardResultStatusSubject.next(val),
  cardResultStatusObservable: () => cardResultStatusSubject.asObservable(),
  addToBasketNext: (val: TransactionLineItemAddRequest) => addToBasketSubject.next(val),
  addToBasketObservable: () => addToBasketSubject.asObservable(),
  addToBasketCompleteNext: (val: { success: boolean }) => addToBasketCompleteSubject.next(val),
  addToBasketCompleteObservable: () => addToBasketCompleteSubject.asObservable(),
  layoutVersionNext: (val: number) => layoutVersionSubject.next(val),
  layoutVersionObservable: () => layoutVersionSubject.asObservable(),
  failedDispensePrintsChangedNext: () => failedDispensePrintsChangedSubject.next(),
  failedDispensePrintsChangedObservable: () => failedDispensePrintsChangedSubject.asObservable(),
  dispenseLogCreatedNext: (val: DispenseLogEvent) => dispenseLogCreatedSubject.next(val),
  dispenseLogCreatedObservable: () => dispenseLogCreatedSubject.asObservable(),
  dispenseLogStateChangedNext: (val: DispenseLogStateChangedEvent) => dispenseLogStateChangedSubject.next(val),
  dispenseLogStateChangedObservable: () => dispenseLogStateChangedSubject.asObservable(),
  dispenseLogVoidedNext: (val: DispenseLogEvent) => dispenseLogVoidedSubject.next(val),
  dispenseLogVoidedObservable: () => dispenseLogVoidedSubject.asObservable(),
  dispenseLogVoidAcknowledgedNext: (val: DispenseLogEvent) => dispenseLogVoidAcknowledgedSubject.next(val),
  dispenseLogVoidAcknowledgedObservable: () => dispenseLogVoidAcknowledgedSubject.asObservable(),
  dispenseLogOverdueNext: (val: DispenseLogOverdueEvent) => dispenseLogOverdueSubject.next(val),
  dispenseLogOverdueObservable: () => dispenseLogOverdueSubject.asObservable(),
  dispenseCompleteNext: (val: DispenseCompleteEvent) => dispenseCompleteSubject.next(val),
  dispenseCompleteObservable: () => dispenseCompleteSubject.asObservable(),
  dispenseReadyNext: (val: DispenseCompleteEvent) => dispenseReadySubject.next(val),
  dispenseReadyObservable: () => dispenseReadySubject.asObservable(),
  cardFailureLockNext: (val: CardFailureLockEvent) => cardFailureLockSubject.next(val),
  cardFailureLockObservable: () => cardFailureLockSubject.asObservable(),
  cardPreAuthSuccessNext: (val: CardPreAuthSuccessEvent) => cardPreAuthSuccessSubject.next(val),
  cardPreAuthSuccessObservable: () => cardPreAuthSuccessSubject.asObservable(),
  cardPreAuthFailureNext: (val: CardFailureLockEvent) => cardPreAuthFailureSubject.next(val),
  cardPreAuthFailureObservable: () => cardPreAuthFailureSubject.asObservable(),
  cardPreAuthCapturedNext: (val: CardPreAuthCapturedEvent) => cardPreAuthCapturedSubject.next(val),
  cardPreAuthCapturedObservable: () => cardPreAuthCapturedSubject.asObservable(),
  cardPreAuthIncrementedNext: (val: CardPreAuthIncrementedEvent) => cardPreAuthIncrementedSubject.next(val),
  cardPreAuthIncrementedObservable: () => cardPreAuthIncrementedSubject.asObservable(),
  cardPreAuthReleasedNext: (val: CardPreAuthReleasedEvent) => cardPreAuthReleasedSubject.next(val),
  cardPreAuthReleasedObservable: () => cardPreAuthReleasedSubject.asObservable(),
};

export interface CardPaymentStatusItem {
  terminalId: string;
  transactionNumber: number;
  success: boolean;
  lastAmount: number;
  dateTimeAdded: number;
  operatorName: string;
  terminalName: string;
}

export interface DispenseLogEvent {
  id: number;
  orderDeviceId: number;
  transactionId: number;
  transactionNumber: number;
}

export interface DispenseLogStateChangedEvent extends DispenseLogEvent {
  state: string;
}

export interface DispenseLogOverdueEvent {
  orderDeviceId: number;
}

export interface CardFailureLockEvent {
  transactionId: number;
  transactionNumber: number;
  sequenceNumber: number;
  operatorId: number;
  operatorName: string;
  amount: number;
  reason: string;
  cardRequestId: number | null;
  pedName: string;
  pedTerminalId: string;
  message: string;
}

export interface DispenseCompleteEvent {
  dispenseLogId: number;
  orderDeviceId: number;
  transactionId: number;
  orderDeviceName: string;
  transactionNumber: number;
  sequenceNumber: number;
  tableNumber: number | null;
}
